import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { PillButton } from '@sorare/core/src/atoms/buttons/PillButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import type { IconDefinition } from '@sorare/core/src/atoms/icons';
import { iconCart } from '@sorare/core/src/atoms/icons/system/iconCart.ts';
import { iconGavel } from '@sorare/core/src/atoms/icons/system/iconGavel.ts';
import { iconLightningBolt } from '@sorare/core/src/atoms/icons/system/iconLightningBolt.ts';
import { iconMarket } from '@sorare/core/src/atoms/icons/system/iconMarket.ts';
import { iconTransfer } from '@sorare/core/src/atoms/icons/system/iconTransfer.ts';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import {
  ANY_SPORT_MARKET_SHOP_AUCTIONS,
  ANY_SPORT_MARKET_SHOP_INSTANT_BUY,
  ANY_SPORT_MARKET_SHOP_MANAGER_SALES,
  ANY_SPORT_MARKET_SHOP_MATCHDAY_PICKS,
  FOOTBALL_MARKET_NEW_SEASON,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useMatchdayPicksTab } from '@sorare/core/src/hooks/market/useMatchdayPicksTab';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';
import { FROM_NEW_SEASON_PARAM } from '@sorare/marketplace/src/lib/newSeason';
import { css } from '@sorare/css';

const bar = css`
  &:is(&) {
    --active-bg-color: var(--c-nd-50);
    --active-fg-color: var(--c-white);
  }

  width: 100%;
  box-sizing: border-box;
  padding: 0 var(--container-padding, var(--double-unit));

  @media ${tabletAndAbove} {
    width: auto;
    padding: 0;
  }
`;

const onMobile = css`
  display: inline-flex;

  @media ${tabletAndAbove} {
    display: none;
  }
`;

const onDesktop = css`
  display: none;

  @media ${tabletAndAbove} {
    display: inline-flex;
  }
`;

type MarketTab = {
  to: string;
  icon: IconDefinition;
  iconColor?: string;
  label: ReactNode;
  alwaysShowLabel?: boolean;
};

export const MarketTabs = () => {
  const { sport, generateSportContextPath } = useSportContext();
  const { show: showMatchdayPicks } = useMatchdayPicksTab({ sport });

  const withFromNewSeason = (path: string) =>
    `${path}?${FROM_NEW_SEASON_PARAM}=true`;

  const tabs: MarketTab[] = [
    {
      to: FOOTBALL_MARKET_NEW_SEASON,
      icon: iconMarket,
      label: (
        <FormattedMessage
          defaultMessage="Home"
          description="Nav Labels - Home"
        />
      ),
    },
    ...(showMatchdayPicks
      ? [
          {
            to: withFromNewSeason(
              generateSportContextPath(ANY_SPORT_MARKET_SHOP_MATCHDAY_PICKS)
            ),
            icon: iconLightningBolt,
            iconColor: 'var(--c-red-400)',
            label: (
              <PillButton size="compact" color="red" asSpan>
                <FormattedMessage defaultMessage="Flash sales" />
              </PillButton>
            ),
            alwaysShowLabel: true,
          },
        ]
      : []),
    {
      to: withFromNewSeason(
        generateSportContextPath(ANY_SPORT_MARKET_SHOP_AUCTIONS)
      ),
      icon: iconGavel,
      label: <FormattedMessage defaultMessage="Auctions" />,
    },
    {
      to: withFromNewSeason(
        generateSportContextPath(ANY_SPORT_MARKET_SHOP_INSTANT_BUY)
      ),
      icon: iconCart,
      label: <FormattedMessage defaultMessage="Instant Buy" />,
    },
    {
      to: withFromNewSeason(
        generateSportContextPath(ANY_SPORT_MARKET_SHOP_MANAGER_SALES)
      ),
      icon: iconTransfer,
      label: <FormattedMessage defaultMessage="Manager Sales" />,
    },
  ];

  return (
    <TabBar variant="primary" fullWidth scrollable={false} className={bar}>
      {tabs.map(t => (
        <Tab key={t.to} to={t.to}>
          {t.alwaysShowLabel ? (
            <Horizontal gap={0.5} center>
              <FontAwesomeIcon
                icon={t.icon}
                fontSize={16}
                color={t.iconColor}
              />
              {t.label}
            </Horizontal>
          ) : (
            <>
              <span className={onMobile}>
                <FontAwesomeIcon icon={t.icon} fontSize={16} />
              </span>
              <span className={onDesktop}>{t.label}</span>
            </>
          )}
        </Tab>
      ))}
    </TabBar>
  );
};
